// AI Website Generation Service
// Uses async job pattern with Supabase Realtime for updates

import { supabase } from './supabase'

interface GenerationResult {
  html: string
  css: string
}

interface GenerationJob {
  generation_id: string
  status: string
}

/**
 * Start a website generation job (returns immediately)
 * The actual generation happens in the background
 */
export async function startGeneration(
  prompt: string, 
  projectId: string, 
  userId: string
): Promise<GenerationJob> {
  const { data, error } = await supabase.functions.invoke('start-generation', {
    body: { prompt, project_id: projectId, user_id: userId },
  })

  if (error) {
    throw new Error(error.message || 'Failed to start generation')
  }

  if (data.error) {
    throw new Error(data.error)
  }

  return {
    generation_id: data.generation_id,
    status: data.status,
  }
}

/**
 * Subscribe to generation updates via Realtime
 * Calls onUpdate when the generation status changes
 * Returns unsubscribe function
 */
export function subscribeToGeneration(
  generationId: string,
  onUpdate: (generation: {
    status: string
    html_output?: string
    css_output?: string
    error_message?: string
  }) => void
): () => void {
  const channel = supabase
    .channel(`generation-${generationId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'generations',
        filter: `id=eq.${generationId}`,
      },
      (payload) => {
        onUpdate(payload.new as any)
      }
    )
    .subscribe()

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel)
  }
}

/**
 * Get current generation status
 */
export async function getGenerationStatus(generationId: string) {
  const { data, error } = await supabase
    .from('generations')
    .select('*')
    .eq('id', generationId)
    .single()

  if (error) throw error
  return data
}

// Keep the old function for backward compatibility (falls back to templates)
export async function generateWebsiteWithAI(_prompt: string): Promise<GenerationResult> {
  throw new Error('Use startGeneration() instead for async generation')
}

// Fallback templates when API is not available
export function generateFallbackWebsite(prompt: string): GenerationResult {
  const lowerPrompt = prompt.toLowerCase()
  
  const isLandingPage = lowerPrompt.includes('landing') || 
                        lowerPrompt.includes('marketing') || 
                        lowerPrompt.includes('saas')
  
  const isPortfolio = lowerPrompt.includes('portfolio') || 
                      lowerPrompt.includes('personal')
  
  const isEcommerce = lowerPrompt.includes('shop') || 
                      lowerPrompt.includes('store') || 
                      lowerPrompt.includes('ecommerce')

  if (isLandingPage) {
    return getLandingPageTemplate()
  } else if (isPortfolio) {
    return getPortfolioTemplate()
  } else if (isEcommerce) {
    return getEcommerceTemplate()
  }
  
  return getDefaultTemplate()
}

function getLandingPageTemplate(): GenerationResult {
  return {
    html: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Modern SaaS Landing Page</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
</head>
<body>
  <nav class="navbar">
    <div class="container nav-content">
      <a href="#" class="logo">
        <span class="logo-icon">◆</span>
        <span>Brand</span>
      </a>
      <ul class="nav-links">
        <li><a href="#features">Features</a></li>
        <li><a href="#pricing">Pricing</a></li>
        <li><a href="#about">About</a></li>
        <li><a href="#" class="btn btn-primary">Get Started</a></li>
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="container hero-content">
      <div class="hero-badge">✨ Now with AI-powered features</div>
      <h1>Build Amazing Products<br><span class="gradient-text">Faster Than Ever</span></h1>
      <p class="hero-description">The all-in-one platform that helps teams ship products 10x faster.</p>
      <div class="hero-buttons">
        <a href="#" class="btn btn-primary btn-lg">Start Free Trial</a>
        <a href="#" class="btn btn-secondary btn-lg">Watch Demo</a>
      </div>
    </div>
  </section>

  <section id="features" class="features">
    <div class="container">
      <div class="section-header">
        <h2>Everything You Need</h2>
        <p>Powerful features to help you build, launch, and scale.</p>
      </div>
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon">⚡</div>
          <h3>Lightning Fast</h3>
          <p>Built for speed with optimized performance.</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">🔒</div>
          <h3>Secure</h3>
          <p>Enterprise-grade security built in.</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">🎨</div>
          <h3>Beautiful</h3>
          <p>Stunning interfaces your users will love.</p>
        </div>
      </div>
    </div>
  </section>
</body>
</html>`,
    css: `* { margin: 0; padding: 0; box-sizing: border-box; }
:root { --primary: #6366f1; --bg: #0f0f12; --text: #fafafa; --muted: #a1a1aa; --border: #27272a; }
body { font-family: 'Inter', sans-serif; background: var(--bg); color: var(--text); }
.container { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }
.navbar { position: fixed; top: 0; left: 0; right: 0; padding: 1rem 0; background: rgba(15,15,18,0.8); backdrop-filter: blur(12px); z-index: 100; }
.nav-content { display: flex; justify-content: space-between; align-items: center; }
.logo { display: flex; align-items: center; gap: 0.5rem; font-weight: 700; color: var(--text); text-decoration: none; }
.logo-icon { color: var(--primary); }
.nav-links { display: flex; align-items: center; gap: 2rem; list-style: none; }
.nav-links a { color: var(--muted); text-decoration: none; transition: color 0.3s; }
.nav-links a:hover { color: var(--text); }
.btn { padding: 0.75rem 1.5rem; border-radius: 8px; font-weight: 500; text-decoration: none; transition: all 0.3s; }
.btn-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); color: white; }
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 40px rgba(99,102,241,0.3); }
.btn-secondary { background: var(--border); color: var(--text); }
.btn-lg { padding: 1rem 2rem; }
.hero { padding: 10rem 0 6rem; text-align: center; }
.hero-badge { display: inline-block; padding: 0.5rem 1rem; background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.3); border-radius: 50px; font-size: 0.875rem; color: var(--primary); margin-bottom: 2rem; }
.hero h1 { font-size: 4rem; font-weight: 700; line-height: 1.1; margin-bottom: 1.5rem; }
.gradient-text { background: linear-gradient(135deg, #6366f1, #d946ef); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.hero-description { font-size: 1.25rem; color: var(--muted); margin-bottom: 2rem; }
.hero-buttons { display: flex; justify-content: center; gap: 1rem; }
.features { padding: 6rem 0; }
.section-header { text-align: center; margin-bottom: 4rem; }
.section-header h2 { font-size: 2.5rem; margin-bottom: 1rem; }
.section-header p { color: var(--muted); }
.features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
.feature-card { background: #18181b; border: 1px solid var(--border); border-radius: 16px; padding: 2rem; transition: all 0.3s; }
.feature-card:hover { transform: translateY(-4px); border-color: var(--primary); }
.feature-icon { font-size: 2rem; margin-bottom: 1rem; }
.feature-card h3 { margin-bottom: 0.5rem; }
.feature-card p { color: var(--muted); font-size: 0.875rem; }
@media (max-width: 768px) { .hero h1 { font-size: 2.5rem; } .features-grid { grid-template-columns: 1fr; } .nav-links { display: none; } }`,
  }
}

function getPortfolioTemplate(): GenerationResult {
  return {
    html: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Portfolio</title>
</head>
<body>
  <header><nav><a href="#" class="logo">JD</a></nav></header>
  <main>
    <section class="hero">
      <h1>Creative Developer & Designer</h1>
      <p>I build beautiful digital experiences.</p>
    </section>
    <section class="work">
      <h2>Selected Work</h2>
      <div class="work-grid">
        <div class="work-card">Project 1</div>
        <div class="work-card">Project 2</div>
      </div>
    </section>
  </main>
</body>
</html>`,
    css: `* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: system-ui, sans-serif; background: #0a0a0a; color: #fff; }
header { padding: 1.5rem 3rem; }
.logo { font-weight: 700; color: #fff; text-decoration: none; }
.hero { min-height: 80vh; display: flex; flex-direction: column; justify-content: center; padding: 0 3rem; }
.hero h1 { font-size: 4rem; margin-bottom: 1rem; }
.hero p { color: #888; font-size: 1.5rem; }
.work { padding: 4rem 3rem; }
.work h2 { color: #888; font-size: 0.875rem; text-transform: uppercase; margin-bottom: 2rem; }
.work-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2rem; }
.work-card { aspect-ratio: 16/10; background: #1a1a1a; border-radius: 16px; display: flex; align-items: center; justify-content: center; }`,
  }
}

function getEcommerceTemplate(): GenerationResult {
  return {
    html: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Shop</title>
</head>
<body>
  <nav><a href="#" class="logo">STORE</a></nav>
  <section class="hero"><h1>New Arrivals</h1><a href="#" class="btn">Shop Now</a></section>
  <section class="products">
    <div class="product-grid">
      <div class="product"><div class="img"></div><h3>Product</h3><p>$99</p></div>
      <div class="product"><div class="img"></div><h3>Product</h3><p>$129</p></div>
    </div>
  </section>
</body>
</html>`,
    css: `* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: system-ui, sans-serif; background: #fafafa; color: #1a1a1a; }
nav { padding: 1.5rem 3rem; background: #fff; }
.logo { font-weight: 700; letter-spacing: 0.1em; color: #1a1a1a; text-decoration: none; }
.hero { text-align: center; padding: 6rem 2rem; background: #1a1a1a; color: #fff; }
.hero h1 { font-size: 3rem; margin-bottom: 2rem; }
.btn { display: inline-block; padding: 1rem 2rem; background: #fff; color: #1a1a1a; text-decoration: none; }
.products { padding: 4rem 3rem; }
.product-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2rem; }
.product { background: #fff; padding: 1rem; }
.product .img { aspect-ratio: 1; background: #eee; margin-bottom: 1rem; }`,
  }
}

function getDefaultTemplate(): GenerationResult {
  return {
    html: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>My Website</title>
</head>
<body>
  <header><nav><h1>Website</h1></nav></header>
  <main>
    <section class="hero">
      <h2>Welcome</h2>
      <p>Start building something amazing.</p>
      <button>Get Started</button>
    </section>
  </main>
</body>
</html>`,
    css: `* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: system-ui, sans-serif; background: #0a0a0a; color: #fff; }
header { padding: 1.5rem 2rem; border-bottom: 1px solid #222; }
.hero { min-height: 80vh; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
.hero h2 { font-size: 3rem; margin-bottom: 1rem; }
.hero p { color: #888; margin-bottom: 2rem; }
.hero button { background: #6366f1; color: #fff; border: none; padding: 1rem 2rem; border-radius: 8px; cursor: pointer; }`,
  }
}
